/**
 * DangoteSDGContribution — SDG Contribution derived from monthly calculation outputs.
 * Route: /corporate/sdg
 * Source: GET /api/dangote/sdg/{period_id}
 * 
 * NOT hardcoded static mapping — all contributions derived from actual monthly data.
 * Direct / Indirect / Conditional classification per GRI/SDG standards.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw, ChevronDown, ChevronUp, Info, CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";
import client from "@/api/client";

const TYPE_COLORS = {
  Direct:      "bg-emerald-100 text-emerald-700 border-emerald-200",
  Indirect:    "bg-blue-100 text-blue-700 border-blue-200",
  Conditional: "bg-amber-100 text-amber-700 border-amber-200",
};

const SDG_COLORS = {
  7:  "#FCC30B", 9: "#F36D25", 11: "#F99D26",
  12: "#BF8B2E", 13: "#3F7E44", 6:  "#26BDE2",
  8:  "#A21942", 15: "#56C02B",
};

function SDGCard({ sdg }) {
  const [open, setOpen] = useState(false);
  const color = SDG_COLORS[sdg.sdg] || "#666";

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white hover:border-slate-300 transition-colors"
      data-testid={`sdg-card-${sdg.sdg}`}>
      <button className="w-full flex items-start justify-between px-5 py-4 text-left"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-3 flex-1 min-w-0 mr-3">
          {/* SDG number badge */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{ backgroundColor: color }}>
            {sdg.sdg}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <p className="text-sm font-bold text-slate-900">SDG {sdg.sdg} — {sdg.name}</p>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${TYPE_COLORS[sdg.type]||TYPE_COLORS.Conditional}`}>
                {sdg.type}
              </span>
            </div>
            <p className="text-xs text-slate-400">{sdg.primary_contribution?.driver || ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {sdg.total_annualised_tco2e > 0 && (
            <div className="text-right">
              <p className="text-sm font-bold text-emerald-700 tabular-nums">
                {sdg.total_annualised_tco2e >= 1000
                  ? `${Math.round(sdg.total_annualised_tco2e/1000)}K`
                  : Math.round(sdg.total_annualised_tco2e).toLocaleString()}
              </p>
              <p className="text-xs text-slate-400">tCO₂e/yr</p>
            </div>
          )}
          {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </div>
      </button>

      {open && sdg.all_contributions?.length > 0 && (
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 space-y-4">
          {sdg.all_contributions.slice(0, 3).map((c, i) => (
            <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${TYPE_COLORS[c.type]||TYPE_COLORS.Conditional}`}>{c.type}</span>
                    <p className="text-xs font-bold text-slate-800">{c.driver}</p>
                  </div>
                  <p className="text-xs text-slate-500">{c.note}</p>
                </div>
                {c.metric_value > 0 && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-slate-900 tabular-nums">{Number(c.metric_value).toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{c.metric_unit}</p>
                  </div>
                )}
              </div>

              {/* Source metrics */}
              <div className="flex items-center gap-2 mt-2">
                <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold">Source metrics:</p>
                {(c.source_metrics || []).map(m => (
                  <code key={m} className="text-[9px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-teal-700 font-mono">{m}</code>
                ))}
              </div>

              {/* Confidence */}
              <div className="flex items-center gap-2 mt-1">
                {c.confidence === "High"   && <span className="text-[9px] font-semibold text-emerald-600">Confidence: High — quantified from calculation</span>}
                {c.confidence === "Medium" && <span className="text-[9px] font-semibold text-amber-600">Confidence: Medium — estimated from activity data</span>}
                {c.confidence === "Low"    && <span className="text-[9px] font-semibold text-slate-500">Confidence: Low — indicative only</span>}
              </div>

              {c.disclosure_note && (
                <div className="mt-2 flex items-start gap-1.5">
                  <Info size={10} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[9px] text-amber-600 italic">{c.disclosure_note}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DangoteSDGContribution() {
  const navigate = useNavigate();
  const { selectedPeriod, period, periods, setSelectedPeriod, consolidated } = useDangote();
  const [sdgData, setSdgData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoading(true);
    client.get(`/dangote/sdg/${selectedPeriod}`)
      .then(r => { setSdgData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedPeriod]);

  const directCount   = sdgData?.by_sdg?.filter(s => s.type === "Direct").length || 0;
  const indirectCount = sdgData?.by_sdg?.filter(s => s.type === "Indirect").length || 0;
  const condCount     = sdgData?.by_sdg?.filter(s => s.type === "Conditional").length || 0;
  const totalTco2eYr  = sdgData?.by_sdg?.reduce((s,x) => s + (x.total_annualised_tco2e||0), 0) || 0;

  if (loading) return (
    <div className="p-6 lg:p-8 space-y-4">
      {[1,2,3].map(i=><div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse"/>)}
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="SDG Contribution" />
          <p className="text-xs text-slate-400 mt-0.5">
            Derived from monthly calculation outputs — not a static mapping
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedPeriod||""} onChange={e => setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200">
            {period?.label}
          </span>
        </div>
      </div>

      {!sdgData?.data_available ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <AlertTriangle size={20} className="text-amber-400 mx-auto mb-2" />
          <p className="text-sm font-bold text-amber-800">No Calculation Data</p>
          <p className="text-xs text-amber-600 mt-1">{sdgData?.message}</p>
          <button onClick={() => navigate("/corporate/reporting")}
            className="mt-3 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl">
            Go to Monthly Reporting
          </button>
        </div>
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard label="SDGs Addressed" value={sdgData.total_contributions} color="text-slate-900" />
            <MetricCard label="Direct Contributions" value={directCount} color="text-emerald-700" />
            <MetricCard label="Indirect / Conditional" value={indirectCount + condCount} color="text-blue-700" />
            <MetricCard label="SDG 13 Impact" value={`${Number(totalTco2eYr/1000).toFixed(0)}K`} unit="tCO₂e/yr" color="text-teal-700" />
          </div>

          {/* Legend */}
          <SectionCard>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4 text-xs">
                {[["Direct","bg-emerald-100 text-emerald-700","Primary project output directly advances this goal"],
                  ["Indirect","bg-blue-100 text-blue-700","Secondary co-benefit from project activities"],
                  ["Conditional","bg-amber-100 text-amber-700","Potential contribution — requires additional evidence"],
                ].map(([l,c,d]) => (
                  <div key={l} className="flex items-center gap-1.5">
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${c}`}>{l}</span>
                    <span className="text-slate-400 hidden lg:block">{d}</span>
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-amber-600 italic">
                Contributions derived from monthly data · Not independently certified · Subject to external sustainability audit
              </p>
              <p className="text-[9px] text-slate-400 italic mt-1">
                Derived from modeled emissions and activity data — not independently verified
              </p>
            </div>
          </SectionCard>

          {/* SDG Cards */}
          <div className="space-y-3">
            {(sdgData.by_sdg || []).map(sdg => (
              <SDGCard key={sdg.sdg} sdg={sdg} />
            ))}
          </div>

          {/* Disclosure */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
            <p className="text-xs font-bold text-amber-700 mb-1">Methodology Disclosure</p>
            <p className="text-xs text-amber-600 italic leading-relaxed">
              SDG contributions are derived from monthly calculation outputs and classified per GRI Standards and SDG
              industry mapping guidelines. Quantified contributions (tCO₂e) trace back to verified emission calculations.
              Physical metrics (tonnes AFR, bagasse, waste) trace back to monthly activity data.
              Indirect and conditional contributions are assessed based on established causal pathways.
              These disclosures have not been independently verified. External sustainability audit is required
              before inclusion in formal SDG or integrated reporting.
            </p>
          </div>

          {/* Action */}
          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Related Pages</p>
            {[
              ["Climate & Circularity", "/corporate/climate"],
              ["Carbon Strategy",       "/corporate/carbon-strategy"],
              ["Reports (SDG Report)",  "/corporate/reports"],
            ].map(([l,p]) => (
              <button key={p} onClick={()=>navigate(p)}
                className="w-full flex items-center justify-between py-2 text-xs text-slate-600 hover:text-teal-700 group">
                {l} <ChevronRight size={11} className="text-slate-300 group-hover:text-teal-500" />
              </button>
            ))}
          </SectionCard>
        </>
      )}
    </div>
  );
}
